import { type QueryKey, type UseQueryResult } from "@tanstack/react-query";

import { isAxiosError } from "axios";

type QueryStatus = UseQueryResult["status"];

// Structural minimum assertQuery relies on; real UseQueryResult values
// satisfy it, and tests can build plain literals without type assertions.
export type QueryLike = {
  status: QueryStatus;
  error: unknown;
  refetch?: () => Promise<unknown>;
};

type SafeErrorPayload = {
  status: number | undefined;
  code: string | undefined;
  method: string | undefined;
  url: string | undefined;
  responseBody: string | undefined;
};

export class QueryStatusAssertionError extends Error {
  public override readonly name = "QueryStatusAssertionError";
  public readonly expectedStatus: QueryStatus;
  public readonly actualStatus: QueryStatus;
  public readonly queryKey: QueryKey | undefined;
  public readonly errorPayload: SafeErrorPayload | undefined;

  public constructor(query: QueryLike, expectedStatus: QueryStatus) {
    super(
      `Query is in status ${query.status} when trying to assert query is in status ${expectedStatus}`,
      query.error ? { cause: query.error } : undefined,
    );
    this.expectedStatus = expectedStatus;
    this.actualStatus = query.status;
    this.queryKey = getQueryKey(query);
    this.errorPayload = extractSafeErrorPayload(query.error);
  }
}

// Copies a compact, header-free view of the underlying request failure onto
// the assertion error so Sentry's extra-error-data shows it without needing
// the chained cause to be serialized. Response bodies are truncated; request
// and response headers (cookies, Authorization) are never included.
function extractSafeErrorPayload(error: unknown): SafeErrorPayload | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }
  return {
    status: error.response?.status,
    code: error.code,
    method: error.config?.method,
    url: error.config?.url,
    responseBody: formatResponseBody(error.response?.data),
  };
}

const MAX_RESPONSE_BODY_LENGTH = 2048;

function formatResponseBody(data: unknown): string | undefined {
  if (data === undefined || data === null) {
    return undefined;
  }
  try {
    const serialized = typeof data === "string" ? data : JSON.stringify(data);
    return serialized.length > MAX_RESPONSE_BODY_LENGTH
      ? `${serialized.slice(0, MAX_RESPONSE_BODY_LENGTH)}… (truncated)`
      : serialized;
  } catch {
    return undefined;
  }
}

function getQueryKey(query: QueryLike): QueryKey | undefined {
  if ("queryKey" in query && Array.isArray(query.queryKey)) {
    return query.queryKey;
  }
  return undefined;
}
